import React, { createContext, useContext, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../store/store';
import { getThemeById } from '../config/themes';
import { Theme } from '../types/theme';

interface ThemeContextType {
  theme: Theme;
  applyTheme: (theme: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: React.ReactNode;
}

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const currentThemeId = useSelector((state: RootState) => state.theme.currentTheme);
  const theme = getThemeById(currentThemeId);

  const applyTheme = (theme: Theme) => {
    const root = document.documentElement;

    // Apply CSS variables
    Object.entries(theme.cssVariables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });

    // Apply additional styling
    root.style.setProperty('--font-primary', theme.branding.font.primary);
    root.style.setProperty('--font-secondary', theme.branding.font.secondary);
    root.style.setProperty('--border-radius-small', theme.branding.borderRadius.small);
    root.style.setProperty('--border-radius-medium', theme.branding.borderRadius.medium);
    root.style.setProperty('--border-radius-large', theme.branding.borderRadius.large);
    root.style.setProperty('--shadow-small', theme.branding.shadows.small);
    root.style.setProperty('--shadow-medium', theme.branding.shadows.medium);
    root.style.setProperty('--shadow-large', theme.branding.shadows.large);

    // Update document title if theme has branding name
    if (theme.branding.name) {
      document.title = `${theme.branding.name} - Voice Ordering`;
    }
  };

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, applyTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};